import React, { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, PaperPlaneTilt, CheckCircle, X, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";

function Field({ label, children, span = 1 }) {
  return <div style={{ gridColumn: `span ${span}` }}><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

const STATUS_PILL = {
  draft: "bg-slate-100 text-slate-700",
  dispatched: "bg-amber-50 text-amber-700",
  received: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

export default function StockTransfers() {
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [whItems, setWhItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState({
    from_warehouse_id: "", to_project_id: "",
    transfer_date: new Date().toISOString().slice(0, 10),
    lines: [], notes: "",
  });

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get("/stock-transfers"),
      api.get("/warehouses"),
      api.get("/projects"),
    ]);
    setRows(a.data); setWarehouses(b.data); setProjects(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (form.from_warehouse_id) {
      api.get("/warehouse-items", { params: { warehouse_id: form.from_warehouse_id } }).then((r) => setWhItems(r.data));
    } else {
      setWhItems([]);
    }
  }, [form.from_warehouse_id]);

  const whMap = Object.fromEntries(warehouses.map((w) => [w.id, `${w.code} · ${w.name}`]));
  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { item_id: "", name: "", sku: "", unit: "nos", qty: 1, unit_cost: 0 }] }));
  const setLine = (idx, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const selectItem = (idx, itemId) => {
    const it = whItems.find((i) => i.id === itemId);
    if (it) setLine(idx, { item_id: it.id, name: it.name, sku: it.sku, unit: it.unit, unit_cost: it.unit_cost });
  };

  const submit = async () => {
    if (!form.from_warehouse_id || !form.to_project_id) return toast.error("Select source warehouse and target project");
    if (form.lines.length === 0) return toast.error("Add at least one material line");
    try {
      await api.post("/stock-transfers", {
        ...form,
        lines: form.lines.map((l) => ({ ...l, qty: Number(l.qty), unit_cost: Number(l.unit_cost) })),
      });
      toast.success("Transfer created (draft)");
      setCreating(false);
      setForm({ from_warehouse_id: "", to_project_id: "", transfer_date: new Date().toISOString().slice(0, 10), lines: [], notes: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const doAction = async (t, action) => {
    try {
      await api.post(`/stock-transfers/${t.id}/${action}`);
      toast.success(`Transfer ${action}ed`);
      load();
      if (viewing?.id === t.id) {
        const r = await api.get(`/stock-transfers/${t.id}`);
        setViewing(r.data);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (t) => {
    if (!window.confirm("Delete this transfer?")) return;
    try { await api.delete(`/stock-transfers/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const totalOf = (t) => (t.lines || []).reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_cost || 0), 0);

  const createSheet = (
    <Sheet open={creating} onOpenChange={setCreating}>
      <SheetTrigger asChild>
        <Button data-testid="create-transfer-btn" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"><Plus size={14} className="mr-1" /> New Transfer</Button>
      </SheetTrigger>
      <SheetContent className="w-[640px] sm:max-w-2xl overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">New stock transfer</SheetTitle></SheetHeader>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <Field label="From warehouse">
            <Select value={form.from_warehouse_id} onValueChange={(v) => setForm({ ...form, from_warehouse_id: v, lines: [] })}>
              <SelectTrigger data-testid="from-warehouse-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="To project (site)">
            <Select value={form.to_project_id} onValueChange={(v) => setForm({ ...form, to_project_id: v })}>
              <SelectTrigger data-testid="to-project-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Transfer date" span={2}><Input type="date" value={form.transfer_date} onChange={(e) => setForm({ ...form, transfer_date: e.target.value })} /></Field>

          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="label-caps">Materials</Label>
              <button onClick={addLine} disabled={!form.from_warehouse_id} className="text-xs text-[#EA580C] font-semibold disabled:opacity-40" data-testid="add-transfer-line">+ ADD LINE</button>
            </div>
            {form.lines.length === 0 ? (
              <div className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-sm">
                {form.from_warehouse_id ? "Add material lines from this warehouse." : "Pick a warehouse first."}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-[2fr_60px_70px_70px_24px] gap-2 text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                  <span>Material</span><span>Unit</span><span>Qty</span><span>Cost</span><span></span>
                </div>
                {form.lines.map((l, i) => {
                  const stock = whItems.find((x) => x.id === l.item_id)?.stock || 0;
                  const over = Number(l.qty) > Number(stock);
                  return (
                    <div key={`ln-${i}`} className="grid grid-cols-[2fr_60px_70px_70px_24px] gap-2 items-center">
                      <div>
                        <Select value={l.item_id || "__none__"} onValueChange={(v) => v !== "__none__" && selectItem(i, v)}>
                          <SelectTrigger data-testid={`line-select-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {whItems.map((x) => (
                              <SelectItem key={x.id} value={x.id}>{x.name} ({Number(x.stock).toFixed(2)} {x.unit} avail)</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input className="text-xs" value={l.unit} readOnly />
                      <Input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className={over ? "border-red-500" : ""} data-testid={`line-qty-${i}`} />
                      <Input type="number" value={l.unit_cost} onChange={(e) => setLine(i, { unit_cost: e.target.value })} />
                      <button onClick={() => removeLine(i)} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <Field label="Notes" span={2}><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="col-span-2">
            <Button onClick={submit} data-testid="save-transfer-btn" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Create draft</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div data-testid="transfers-page">
      <PageHeader
        title="Stock Transfers"
        subtitle="Move material from a warehouse to a project site. Two-step workflow: dispatch → receive."
        module="Procurement · MM"
        actions={createSheet}
      />

      <div className="p-6 md:p-8">
        {rows.length === 0 ? (
          <EmptyState title="No transfers yet" description="Create a transfer to move material from your warehouse to a project site. Dispatch deducts warehouse stock; Receive adds it to the project inventory." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="transfers-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="label-caps">Transfer #</TableHead>
                  <TableHead className="label-caps">From</TableHead>
                  <TableHead className="label-caps">To Project</TableHead>
                  <TableHead className="label-caps">Date</TableHead>
                  <TableHead className="label-caps text-right">Lines</TableHead>
                  <TableHead className="label-caps text-right">Value</TableHead>
                  <TableHead className="label-caps">Status</TableHead>
                  <TableHead className="w-40" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id} onClick={() => setViewing(t)} className="cursor-pointer hover:bg-slate-50" data-testid={`transfer-row-${t.id}`}>
                    <TableCell className="font-mono-num text-[#EA580C] font-semibold">{t.transfer_no}</TableCell>
                    <TableCell className="text-xs text-slate-700">{whMap[t.from_warehouse_id] || "—"}</TableCell>
                    <TableCell className="text-xs">{projMap[t.to_project_id] || "—"}</TableCell>
                    <TableCell className="font-mono-num text-xs">{fmtDate(t.transfer_date)}</TableCell>
                    <TableCell className="text-right font-mono-num">{(t.lines || []).length}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(totalOf(t))}</TableCell>
                    <TableCell><span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-widest font-semibold ${STATUS_PILL[t.status]}`}>{t.status}</span></TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        {t.status === "draft" && (
                          <button onClick={() => doAction(t, "dispatch")} title="Dispatch" data-testid={`dispatch-${t.id}`} className="text-amber-600 hover:text-amber-700 p-1"><PaperPlaneTilt size={14} /></button>
                        )}
                        {t.status === "dispatched" && (
                          <button onClick={() => doAction(t, "receive")} title="Receive" data-testid={`receive-${t.id}`} className="text-emerald-600 hover:text-emerald-700 p-1"><CheckCircle size={14} /></button>
                        )}
                        {(t.status === "draft" || t.status === "dispatched") && (
                          <button onClick={() => doAction(t, "cancel")} title="Cancel" className="text-slate-500 hover:text-red-600 p-1"><X size={13} /></button>
                        )}
                        {t.status === "draft" && (
                          <button onClick={() => del(t)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={13} /></button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-3">
                  <Truck size={20} className="text-[#EA580C]" />
                  <span className="font-mono text-[#EA580C]">{viewing.transfer_no}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-widest font-semibold ${STATUS_PILL[viewing.status]}`}>{viewing.status}</span>
                </DialogTitle>
                <div className="text-xs text-slate-500 flex flex-wrap gap-x-4">
                  <span>From: <strong>{whMap[viewing.from_warehouse_id] || "—"}</strong></span>
                  <span>To: <strong>{projMap[viewing.to_project_id] || "—"}</strong></span>
                  <span>Date: {fmtDate(viewing.transfer_date)}</span>
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <div className="label-caps mb-2">Material lines</div>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50 text-xs">
                      <TableHead className="label-caps">Material</TableHead>
                      <TableHead className="label-caps">SKU</TableHead>
                      <TableHead className="label-caps text-right">Qty</TableHead>
                      <TableHead className="label-caps text-right">Cost</TableHead>
                      <TableHead className="label-caps text-right">Line total</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>{(viewing.lines || []).map((l, i) => (
                      <TableRow key={`vw-${i}`}>
                        <TableCell className="text-sm">{l.name}</TableCell>
                        <TableCell className="font-mono-num text-xs">{l.sku || "—"}</TableCell>
                        <TableCell className="text-right font-mono-num">{Number(l.qty).toFixed(2)} {l.unit}</TableCell>
                        <TableCell className="text-right font-mono-num text-xs">{fmtMoney(l.unit_cost)}</TableCell>
                        <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(Number(l.qty) * Number(l.unit_cost))}</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </div>
                <div className="border border-slate-200 rounded-sm p-3 text-xs text-slate-600 space-y-1">
                  <div>Created: <span className="font-mono-num">{fmtDate(viewing.created_at)}</span></div>
                  {viewing.dispatched_at && <div>Dispatched: <span className="font-mono-num">{fmtDate(viewing.dispatched_at)}</span></div>}
                  {viewing.received_at && <div>Received: <span className="font-mono-num">{fmtDate(viewing.received_at)}</span></div>}
                  {viewing.notes && <div>Notes: {viewing.notes}</div>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
